import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { siteConfig, categories, articles } from '@/mocks/siteData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === slug),
    [slug],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
              <i className="ri-folder-open-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory.name },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: currentCategory.description,
    url: `${siteConfig.url}/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${siteConfig.url}/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <PageMeta
        title={`${currentCategory.name} - ${siteConfig.name}`}
        description={currentCategory.description}
        canonical={`${siteConfig.url}/category/${currentCategory.slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Hero banner for category */}
          <div className="py-8 md:py-10">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-600 text-sm mt-1">
                  {currentCategory.description} · 共 {categoryArticles.length} 篇文章
                </p>
              </div>
            </div>
          </div>

          {/* Category Switcher */}
          <div className="mb-8 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => navigate(`/category/${cat.slug}`)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Articles List */}
          <div className="pb-14 md:pb-20">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        width="800"
                        height="480"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2 text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h2 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}